import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";
import { MEALS, fdate, fmt, num, run, useLoad, weekday } from "../lib/util";
import { Alert, Badge, Btn, Field, Input, ProductSelect, Select, SectionTitle, Table, Empty, cellCls } from "../components/kit";
import { useSchool } from "./ctx";
import { Downloads } from "./Forms";

function MenuDialog({ initial, onClose, onSaved }) {
  const { products, month } = useSchool();
  const [f, setF] = useState({ date: `${month}-01`, meal: "almoco", preparation: "", ingredients: [], notes: "", ...initial });
  const setIng = (i, v) => setF({ ...f, ingredients: f.ingredients.map((x, j) => (j === i ? v : x)) });
  const save = async () => {
    const body = { date: f.date, meal: f.meal, preparation: f.preparation, notes: f.notes || null, version: f.version ?? null,
      ingredients: f.ingredients.map((x) => ({ product_id: x.product_id || null, per_capita: x.per_capita ?? null, planned_qty: x.planned_qty ?? null, note: x.note || null })) };
    if (await run(() => (f.id ? api.put(`/s/menu/${f.id}`, body) : api.post("/s/menu", body)), "Preparação salva")) {
      onSaved();
      onClose();
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="menu-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Editar preparação" : "Nova preparação"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Data"><Input data-testid="menu-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Refeição"><Select data-testid="menu-meal-select" value={f.meal} onChange={(e) => setF({ ...f, meal: e.target.value })} options={Object.entries(MEALS)} /></Field>
          <Field label="Preparação"><Input data-testid="menu-preparation-input" value={f.preparation} onChange={(e) => setF({ ...f, preparation: e.target.value })} /></Field>
        </div>
        <div className="mt-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Gêneros utilizados</div>
          <table className="w-full text-sm mapia-table border rounded-lg">
            <thead><tr><th>Produto do estoque</th><th>Per capita (un. estoque/pessoa)</th><th>Ou quantidade planejada do dia</th><th /></tr></thead>
            <tbody>
              {f.ingredients.map((x, i) => (
                <tr key={i}>
                  <td><ProductSelect testid={`menu-ing-product-${i}`} products={products} value={x.product_id} allowCreate onChange={(id) => setIng(i, { ...x, product_id: id })} /></td>
                  <td><input data-testid={`menu-ing-pc-${i}`} type="number" step="any" className={cellCls} value={x.per_capita ?? ""} onChange={(e) => setIng(i, { ...x, per_capita: num(e.target.value) })} /></td>
                  <td><input type="number" step="any" className={cellCls} value={x.planned_qty ?? ""} onChange={(e) => setIng(i, { ...x, planned_qty: num(e.target.value) })} /></td>
                  <td><button onClick={() => setF({ ...f, ingredients: f.ingredients.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-red-500" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Btn variant="outline" className="mt-2" data-testid="menu-add-ingredient" onClick={() => setF({ ...f, ingredients: [...f.ingredients, { product_id: null, per_capita: null, planned_qty: null }] })}><Plus className="w-4 h-4" />Adicionar gênero</Btn>
          <p className="text-xs text-slate-500 mt-2">Vincule apenas os gêneros realmente retirados do estoque. Produto comprado pronto (ex.: minibolo) não gera consumo de farinha. A quantidade planejada, quando informada, substitui o cálculo per capita × comensais.</p>
        </div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="menu-save-button" onClick={save}>Salvar</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubRow({ it, items, products, onDone }) {
  const save = async (from, to) => {
    if (await run(() => api.put(`/s/menu/${it.id}/replacement`, { from_product_id: from, to_product_id: to || null }), to ? "Produto substituto registrado" : "Substituto removido")) onDone();
  };
  return (
    <div className="mt-1 space-y-1 font-normal" data-testid={`menu-substitution-${it.id}`}>
      {items.map((y) => (
        <div key={y.product_id} className="flex flex-wrap items-center gap-2 text-xs">
          <Badge tone={y.to_product_id ? "green" : "amber"}>Substituição: falta de {y.product}</Badge>
          {!y.to_product_id && y.suggested_product_id && (
            <Btn variant="outline" className="!h-7 !px-2 text-xs" data-testid={`menu-suggestion-${it.id}-${y.product_id}`} onClick={() => save(y.product_id, y.suggested_product_id)}>
              Sugestão: {y.suggested_product} · Confirmar
            </Btn>
          )}
          <span className="text-slate-500">usado no lugar:</span>
          <select data-testid={`menu-replacement-${it.id}-${y.product_id}`} className={`${cellCls} max-w-[12rem]`} value={y.to_product_id || ""} onChange={(e) => save(y.product_id, e.target.value)}>
            <option value="">— não informado —</option>
            {products.filter((p) => p.id !== y.product_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

export default function MenuEditor() {
  const { month, tick, bump, products, stock } = useSchool();
  const [items, reload] = useLoad(async () => (await api.get("/s/menu", { params: { month } })).data, [month, tick]);
  const [edit, setEdit] = useState(null);
  const pname = (id) => products.find((p) => p.id === id)?.name;
  const del = async (it) => {
    if (window.confirm("Remover esta preparação?") && (await run(() => api.delete(`/s/menu/${it.id}`), "Removida"))) { reload(); bump(); }
  };
  const days = stock?.days || {};
  const pending = (stock?.substitutions || []).reduce((n, x) => n + (x.items || []).filter((y) => !y.to_product_id && y.suggested_product_id).length, 0);
  const acceptAll = async () => {
    const r = await run(() => api.post("/s/menu/accept-suggestions", null, { params: { month } }));
    if (r) { toast.success(`${r.data.confirmed} substituto(s) confirmado(s)`); bump(); }
  };
  const subs = Object.fromEntries((stock?.substitutions || []).map((x) => [`${x.date}|${x.meal}|${x.preparation}`, x.items || []]));
  const sorted = [...(items || [])].sort((a, b) => (a.date + Object.keys(MEALS).indexOf(a.meal)).localeCompare(b.date + Object.keys(MEALS).indexOf(b.meal)));
  return (
    <div className="space-y-4">
      <SectionTitle title="Cardápio diário" subtitle="Preparações por dia e refeição, com os gêneros de estoque relacionados.">
        <Btn data-testid="new-menu-button" onClick={() => setEdit({})}><Plus className="w-4 h-4" />Nova preparação</Btn>
        <Downloads form="cardapio_diario" label="cardápio" />
        {pending > 0 && <Btn variant="outline" data-testid="accept-all-suggestions" onClick={acceptAll}>Confirmar {pending} sugestão(ões) de substituto</Btn>}
      </SectionTitle>
      <Alert tone="info">Comensais por dia: distribuídos a partir do total de comensais de cada refeição, com variação de até 10% para mais ou para menos, sem passar do total de comensais da escola. Base: {Object.entries(MEALS).map(([k, l]) => `${l}: ${stock?.meal_counts?.[k] ?? "pendente"}`).join(" · ")}</Alert>
      <Table testid="menu-table">
        <thead><tr><th>Data</th><th>Refeição</th><th>Preparação</th><th className="num">Comensais</th><th>Atendimento</th><th /></tr></thead>
        <tbody>
          {sorted.map((it) => {
            const served = it.date in days && !days[it.date].includes(it.meal);
            return (
              <tr key={it.id}>
                <td className="whitespace-nowrap">{fdate(it.date)} <span className="text-xs text-slate-400">{weekday(it.date)}</span></td>
                <td>{MEALS[it.meal]}</td>
                <td className="font-semibold">
                  {it.preparation}
                  {subs[`${it.date}|${it.meal}|${it.preparation}`] && (
                    <SubRow it={it} items={subs[`${it.date}|${it.meal}|${it.preparation}`]} products={products} onDone={bump} />
                  )}
                </td>
                <td className="num" data-testid={`menu-count-${it.id}`}>{served ? stock?.daily_meal_counts?.[it.date]?.[it.meal] ?? <Badge tone="amber">pendente</Badge> : "—"}</td>
                <td>{served ? <Badge tone="green">Sim</Badge> : <Badge>Sem atendimento</Badge>}</td>
                <td className="whitespace-nowrap">
                  <Btn variant="ghost" data-testid={`edit-menu-${it.id}`} onClick={() => setEdit(it)}><Pencil className="w-4 h-4" /></Btn>
                  <Btn variant="ghost" onClick={() => del(it)}><Trash2 className="w-4 h-4 text-red-600" /></Btn>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {items && !items.length && <Empty>Nenhuma preparação neste mês. Importe o cardápio SEEDUC (menu 2) ou cadastre manualmente.</Empty>}
      {edit && <MenuDialog initial={edit} onClose={() => setEdit(null)} onSaved={() => { reload(); bump(); }} />}
    </div>
  );
}
